import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Send, Plus, Edit2 } from 'lucide-react'
import { notificationsApi } from '../../lib/api'
import DataTable, { Column } from '../../components/shared/DataTable'
import StatusBadge from '../../components/shared/StatusBadge'
import LoadingState from '../../components/shared/LoadingState'
import { useToast } from '../../hooks/useToast'
import { usePermission } from '../../hooks/usePermission'
import { formatDatetime } from '../../lib/utils'
import type { NotificationTemplate, NotificationLog, NotificationChannel } from '../../types'
import NoticeBanner from '../../components/shared/NoticeBanner'
import { tx } from '../../lib/i18n'

const channelOptions: { value: NotificationChannel; label: string }[] = [
  { value: 'email', label: 'Email (ส่งจริง)' },
  { value: 'in_app', label: 'In-App (กระดิ่งหลังร้าน + push แอป)' },
  { value: 'line', label: 'LINE (OA ของ Queza → เจ้าของร้าน)' },
  { value: 'sms', label: 'SMS (บันทึกเท่านั้น)' },
]

const NotificationsPage: React.FC = () => {
  const queryClient = useQueryClient()
  const { showToast } = useToast()
  const { can } = usePermission()

  const [activeTab, setActiveTab] = useState<'send' | 'templates' | 'logs'>('send')
  const [editingTemplate, setEditingTemplate] = useState<NotificationTemplate | null | 'new'>(null)

  // Send form
  const [sendForm, setSendForm] = useState({
    title: '',
    body: '',
    channels: [] as NotificationChannel[],
    targetType: 'all' as 'all' | 'specific',
    shopIds: '',
    scheduledAt: '',
  })

  const { data: templatesData, isLoading: templatesLoading } = useQuery({
    queryKey: ['notification-templates'],
    queryFn: () => notificationsApi.listTemplates().then((r) => r.data.data),
  })

  const { data: logsData, isLoading: logsLoading } = useQuery({
    queryKey: ['notification-logs'],
    queryFn: () => notificationsApi.listMessages({ limit: 50 }).then((r) => r.data.data),
  })

  const templates = templatesData ?? []
  const logs = Array.isArray(logsData) ? logsData : []

  const sendMutation = useMutation({
    mutationFn: () =>
      notificationsApi.send({
        title: sendForm.title,
        body: sendForm.body,
        channels: sendForm.channels,
        targetType: sendForm.targetType,
        shopIds: sendForm.targetType === 'specific' ? sendForm.shopIds.split(',').map((s) => s.trim()) : undefined,
        scheduledAt: null,
      }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['notification-logs'] })
      const d = (res.data.data ?? {}) as { sent?: number; failed?: number; recorded?: number }
      showToast({
        type: d.failed ? 'error' : 'success',
        message: `ส่งสำเร็จ ${d.sent ?? 0} · ล้มเหลว ${d.failed ?? 0} · บันทึกไว้ ${d.recorded ?? 0}`,
      })
      setSendForm({ title: '', body: '', channels: [], targetType: 'all', shopIds: '', scheduledAt: '' })
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (e: any) => showToast({ type: 'error', message: e?.response?.data?.error ?? 'Failed to send notification.' }),
  })

  const saveTemplateMutation = useMutation({
    mutationFn: (data: Partial<NotificationTemplate>) =>
      editingTemplate && editingTemplate !== 'new'
        ? notificationsApi.updateTemplate(editingTemplate.id, data)
        : notificationsApi.createTemplate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-templates'] })
      showToast({ type: 'success', message: tx('Template saved.') })
      setEditingTemplate(null)
    },
    onError: () => showToast({ type: 'error', message: tx('Failed to save template.') }),
  })

  const toggleChannel = (ch: NotificationChannel) => {
    setSendForm((f) => ({
      ...f,
      channels: f.channels.includes(ch) ? f.channels.filter((c) => c !== ch) : [...f.channels, ch],
    }))
  }

  const templateColumns: Column<NotificationTemplate>[] = [
    { key: 'name', header: tx('Name'), render: (r) => <span className="font-mono text-sm text-gray-800">{r.name}</span> },
    { key: 'channel', header: tx('Channel'), render: (r) => <StatusBadge status="active" label={r.channel.replace('_', ' ')} /> },
    { key: 'subject', header: tx('Subject'), render: (r) => <span className="text-gray-500 text-sm">{r.subject ?? <em className="text-gray-300">{tx('No subject')}</em>}</span> },
    {
      key: 'isActive', header: tx('Active'),
      render: (r) => <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${r.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{r.isActive ? tx('Active') : tx('Inactive')}</span>,
    },
    {
      key: 'actions', header: '',
      render: (r) => (
        can('notifications.update') && <button onClick={() => setEditingTemplate(r)} className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors">
          <Edit2 className="h-4 w-4" />
        </button>
      ),
    },
  ]

  const logColumns: Column<NotificationLog>[] = [
    { key: 'sentAt', header: tx('Sent At'), render: (r) => <span className="text-gray-500 text-sm">{formatDatetime(r.sentAt)}</span> },
    { key: 'title', header: tx('Title'), render: (r) => <span className="font-medium text-gray-900">{r.title}</span> },
    { key: 'channel', header: tx('Channel'), render: (r) => <StatusBadge status="active" label={r.channel.replace('_', ' ')} /> },
    { key: 'status', header: tx('Status'), render: (r) => <StatusBadge status={r.status} /> },
    { key: 'recipientCount', header: tx('Recipients'), render: (r) => <span className="text-gray-700">{r.recipientCount}</span> },
    { key: 'sentCount', header: tx('Sent'), render: (r) => <span className="text-green-600 font-medium">{r.sentCount}{r.recordedCount ? <span className="text-gray-400 font-normal text-xs"> (+{r.recordedCount} {tx('recorded')})</span> : null}</span> },
    { key: 'failedCount', header: tx('Failed'), render: (r) => <span className={r.failedCount > 0 ? 'text-red-600 font-medium' : 'text-gray-400'}>{r.failedCount}</span> },
  ]

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-bold text-gray-900">{tx('Notifications')}</h1>
        <p className="text-sm text-gray-500 mt-0.5">{tx('Send notifications, manage templates, and view delivery logs')}</p>
      </div>

      <NoticeBanner tone="ok">
        <b>เชื่อมกับแอปร้านแล้ว</b> — <b>In-App</b> = กระดิ่งในหลังร้านเว็บ + push เข้าแอปมือถือทุกเครื่องของร้าน · <b>LINE</b> = ส่งผ่าน LINE OA ของ Queza ถึงเจ้าของ/แอดมินที่ล็อกอินด้วย LINE · <b>Email</b> = SMTP ใน System Settings → อีเมลร้าน · SMS ยังแค่บันทึก (ดูผลรายร้านที่แท็บประวัติการส่ง)
      </NoticeBanner>

      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
        {(['send', 'templates', 'logs'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-1.5 text-sm font-medium rounded-lg capitalize transition-all ${activeTab === tab ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
          >
            {tab === 'send' ? 'Send Notification' : tab === 'logs' ? 'Delivery Logs' : tx('Templates')}
          </button>
        ))}
      </div>

      {/* Send Tab */}
      {activeTab === 'send' && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-5 max-w-2xl">
          <h2 className="text-sm font-semibold text-gray-800">{tx('Send New Notification')}</h2>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{tx('Title')}</label>
            <input type="text" value={sendForm.title} onChange={(e) => setSendForm((f) => ({ ...f, title: e.target.value }))} placeholder={tx('Notification title...')} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{tx('Body')}</label>
            <textarea rows={4} value={sendForm.body} onChange={(e) => setSendForm((f) => ({ ...f, body: e.target.value }))} placeholder={tx('Notification message...')} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{tx('Channels')}</label>
            <div className="flex flex-wrap gap-2">
              {channelOptions.map(({ value, label }) => (
                <button
                  key={value}
                  onClick={() => toggleChannel(value)}
                  className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${sendForm.channels.includes(value) ? 'bg-indigo-600 text-white border-indigo-600' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{tx('Target')}</label>
            <div className="flex gap-3">
              {(['all', 'specific'] as const).map((t) => (
                <button
                  key={t}
                  onClick={() => setSendForm((f) => ({ ...f, targetType: t }))}
                  className={`px-3 py-1.5 text-sm rounded-lg border capitalize transition-colors ${sendForm.targetType === t ? 'bg-indigo-600 text-white border-indigo-600' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                >
                  {t === 'all' ? tx('All Shops') : tx('Specific Shops')}
                </button>
              ))}
            </div>
            {sendForm.targetType === 'specific' && (
              <input type="text" className="mt-2 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" placeholder="Shop IDs comma-separated: shop-1, shop-2..." value={sendForm.shopIds} onChange={(e) => setSendForm((f) => ({ ...f, shopIds: e.target.value }))} />
            )}
          </div>
          {can('notifications.send') && (
          <button
            onClick={() => sendMutation.mutate()}
            disabled={!sendForm.title || !sendForm.body || sendForm.channels.length === 0 || sendMutation.isPending}
            className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
          >
            <Send className="h-4 w-4" />
            {sendMutation.isPending ? tx('Sending...') : tx('Send Now')}
          </button>
          )}
        </div>
      )}

      {/* Templates Tab */}
      {activeTab === 'templates' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            {can('notifications.create') && (
            <button onClick={() => setEditingTemplate('new')} className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors">
              <Plus className="h-4 w-4" />{tx('Create Template')}
            </button>
            )}
          </div>
          {templatesLoading ? <LoadingState variant="skeleton" /> : (
            <DataTable columns={templateColumns} data={templates} emptyMessage={tx('No templates found.')} />
          )}
        </div>
      )}

      {/* Logs Tab */}
      {activeTab === 'logs' && (
        logsLoading ? <LoadingState variant="skeleton" /> : (
          <DataTable columns={logColumns} data={logs} emptyMessage={tx('No logs found.')} />
        )
      )}

      {/* Template form modal */}
      {editingTemplate !== null && (
        <TemplateFormModal
          template={editingTemplate === 'new' ? null : editingTemplate}
          onClose={() => setEditingTemplate(null)}
          onSave={(data) => saveTemplateMutation.mutate(data)}
          isLoading={saveTemplateMutation.isPending}
        />
      )}
    </div>
  )
}

const TemplateFormModal: React.FC<{
  template: NotificationTemplate | null
  onClose: () => void
  onSave: (data: Partial<NotificationTemplate>) => void
  isLoading?: boolean
}> = ({ template, onClose, onSave, isLoading }) => {
  const [form, setForm] = useState({
    name: template?.name ?? '',
    channel: template?.channel ?? 'email' as NotificationChannel,
    subject: template?.subject ?? '',
    body: template?.body ?? '',
    isActive: template?.isActive ?? true,
  })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 p-6">
        <h3 className="text-base font-semibold text-gray-900 mb-4">{template ? 'Edit Template' : tx('Create Template')}</h3>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{tx('Name (snake_case)')}</label>
            <input type="text" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" placeholder="payment_failed" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{tx('Channel')}</label>
            <select value={form.channel} onChange={(e) => setForm((f) => ({ ...f, channel: e.target.value as NotificationChannel }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
              {channelOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          {form.channel === 'email' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{tx('Subject')}</label>
              <input type="text" value={form.subject} onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" placeholder={tx('Email subject...')} />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Body (use {`{{variable}}`} for dynamic values)</label>
            <textarea rows={4} value={form.body} onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none" />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.isActive} onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))} className="h-4 w-4 rounded border-gray-300 text-indigo-600" />
            <span className="text-sm font-medium text-gray-700">{tx('Template is Active')}</span>
          </label>
        </div>
        <div className="flex gap-3 mt-5 justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">{tx('Cancel')}</button>
          <button onClick={() => onSave(form)} disabled={isLoading} className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">
            {isLoading ? tx('Saving...') : 'Save Template'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default NotificationsPage
